import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_KEYS } from "./query-keys";
import { toast } from "sonner";
import { DEFAULT_FEATURES } from "@/lib/user-settings-types";

export const useUserSettingsQuery = (userId?: string, currentUserId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.userSettings(userId),
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      
      if (!data && userId === currentUserId) {
        const { data: newData, error: insertError } = await supabase
          .from('user_settings')
          .upsert({ 
            user_id: userId, 
            features: DEFAULT_FEATURES,
            active_session_id: null 
          }, { onConflict: 'user_id' })
          .select()
          .single();

        if (insertError) throw insertError;
        return newData;
      }
      
      return data;
    },
    enabled: !!userId,
  });
};

export const useUpdateUserSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, features, active_session_id }: { userId: string; features?: any, active_session_id?: string | null }) => {
      const updateData: any = { user_id: userId };
      if (features !== undefined) updateData.features = features;
      if (active_session_id !== undefined) updateData.active_session_id = active_session_id;

      const { data, error } = await supabase
        .from("user_settings")
        .upsert(updateData, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userSettings(data.user_id) });
    },
    onError: (error: any) => {
      toast.error(`Error updating settings: ${error.message}`);
    },
  });
};
